import { useEffect, useState } from 'react';
import { PaginationFooter, type PaginationFooterProps } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@/components/ui/skeleton';
import { resolvePageSize, visiblePageSizes } from './page-sizes';

/**
 * ListPaginationFooter — the DS `PaginationFooter` (its look and its "1-25 of 60" wording) plus two behaviours the
 * list pagers had before the restyle (PR #1241) that the DS footer has no prop for yet:
 *   - `isFetching`: the pager is off while a page loads. A disabled fieldset turns off every footer button, and the
 *     handlers ignore a change that still gets through mid-load.
 *   - `emptyText`: with no records the footer reads e.g. "No invoices" instead of "0 of 0 records".
 * A `disabled` / `emptyLabel` prop on the DS footer is the long-term home for both; this wrapper goes when it lands.
 */
export interface ListPaginationFooterProps<T extends number = number> extends PaginationFooterProps<T> {
  /** A page is loading: page and page-size changes are off until it lands. */
  isFetching?: boolean;
  /** Shown in place of the footer when there are no records, e.g. "No invoices". */
  emptyText?: string;
}

export function ListPaginationFooter<T extends number = number>({
  isFetching = false,
  emptyText,
  onPageChange,
  onPageSizeChange,
  ...footer
}: ListPaginationFooterProps<T>) {
  // While a page loads the list often has no count yet (0 records): keep the last known count and pages so the footer
  // stays put between pages, and show a skeleton on the very first load instead of "0 of 0 records".
  const [known, setKnown] = useState<{ totalRecords: number; totalPages: number } | null>(null);
  if (!isFetching && (known?.totalRecords !== footer.totalRecords || known.totalPages !== footer.totalPages)) {
    setKnown({ totalRecords: footer.totalRecords, totalPages: footer.totalPages });
  }
  const counts = isFetching && footer.totalRecords === 0 && known ? known : footer;
  const size = resolvePageSize(footer.pageSizes, counts.totalRecords, footer.pageSize, isFetching);
  useEffect(() => {
    if (size.request !== null) onPageSizeChange(size.request);
  }, [size.request, onPageSizeChange]);

  if (isFetching && footer.totalRecords === 0 && !known) {
    return (
      <div
        aria-busy="true"
        data-testid="list-pagination-skeleton"
        className="flex items-center justify-between border-t border-border-default px-6 py-3"
      >
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-28" />
        </div>
        <Skeleton className="h-7 w-40" />
      </div>
    );
  }
  if (emptyText && counts.totalRecords === 0) {
    return (
      <p className="border-t border-border-default px-6 py-3 text-sm text-text-muted">
        {emptyText}
      </p>
    );
  }
  return (
    <fieldset disabled={isFetching} aria-busy={isFetching} className="contents">
      <PaginationFooter
        {...footer}
        totalRecords={counts.totalRecords}
        totalPages={counts.totalPages}
        pageSize={size.shown}
        pageSizes={visiblePageSizes(footer.pageSizes, counts.totalRecords)}
        onPageChange={(page) => {
          if (!isFetching) onPageChange(page);
        }}
        onPageSizeChange={(next) => {
          if (!isFetching) onPageSizeChange(next);
        }}
      />
    </fieldset>
  );
}
